import { cn } from "@/lib/utils";
import React from "react";

type VisualizerState = "listening" | "idle" | "speaking" | "thinking";
interface Props {
  readonly state: VisualizerState;
  readonly barWidth: number;
  readonly minBarHeight: number;
  readonly maxBarHeight: number;
  readonly thinkingColor: `bg-${string}` | `bg-${string}-${number}`;
  readonly thinkingColorShadow?:
    | `shadow-lg-${string}`
    | `shadow-lg-${string}-${number}`;
  readonly frequencyColor: `bg-${string}` | `bg-${string}-${number}`;
  readonly frequencies: Float32Array[];
  readonly borderRadius: number;
  readonly gap: number;
}

/**
 * Renders an audio visualizer component.
 *
 * @component
 * @param {Props} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */
export function AudioVisualizer(props: Props) {
  const summedFrequencies = props.frequencies.map((bandFrequencies) => {
    const sum = bandFrequencies.reduce((a, b) => a + b, 0);
    return Math.sqrt(sum / bandFrequencies.length);
  });

  const [thinkingIndex, setThinkingIndex] = React.useState(
    Math.floor(summedFrequencies.length / 2),
  );
  const [thinkingDirection, setThinkingDirection] = React.useState<
    "left" | "right"
  >("right");

  /**
   * Executes a timeout function that updates the thinking direction and index based on the current state.
   * If the thinking direction is "right", it increments the thinking index until it reaches the end of the summed frequencies array.
   * If the thinking direction is "left", it decrements the thinking index until it reaches 0.
   * Once the timeout is triggered, the thinking direction and index are updated accordingly.
   * @param {number} thinkingIndex - The current thinking index.
   * @param {string} thinkingDirection - The current thinking direction.
   * @param {number[]} summedFrequencies - The array of summed frequencies.
   * @param {number} timeoutDuration - The duration of the timeout in milliseconds.
   */
  React.useEffect(
    function updateThinkingIndex() {
      if (props.state !== "thinking") {
        setThinkingIndex(Math.floor(summedFrequencies.length / 2));
        return;
      }

      const timeout = setTimeout(() => {
        if (thinkingDirection === "right") {
          if (thinkingIndex === summedFrequencies.length - 1) {
            setThinkingDirection("left");
            setThinkingIndex((prev) => prev - 1);
          } else {
            setThinkingIndex((prev) => prev + 1);
          }
        } else {
          if (thinkingIndex === 0) {
            setThinkingDirection("right");
            setThinkingIndex((prev) => prev + 1);
          } else {
            setThinkingIndex((prev) => prev - 1);
          }
        }
      }, 200);

      return () => clearTimeout(timeout);
    },
    [props.state, summedFrequencies.length, thinkingDirection, thinkingIndex],
  );

  return (
    <div className={cn("flex flex-row items-center", `gap-[${props.gap}px]`)}>
      {summedFrequencies.map((frequency, index) => {
        return (
          <FrequencyBar
            thinkingIndex={thinkingIndex}
            key={index}
            index={index}
            summedFrequencies={summedFrequencies}
            frequency={frequency}
            state={props.state}
            barWidth={props.barWidth}
            minBarHeight={props.minBarHeight}
            maxBarHeight={props.maxBarHeight}
            borderRadius={props.borderRadius}
            thinkingColor={props.thinkingColor}
            thinkingColorShadow={props.thinkingColorShadow}
            frequencyColor={props.frequencyColor}
          />
        );
      })}
    </div>
  );
}

interface FrequencyBarProps
  extends Pick<
    Props,
    | "barWidth"
    | "minBarHeight"
    | "maxBarHeight"
    | "thinkingColor"
    | "thinkingColorShadow"
    | "frequencyColor"
    | "borderRadius"
  > {
  readonly frequency: number;
  readonly state: VisualizerState;
  readonly index: number;
  readonly thinkingIndex: number;
  readonly summedFrequencies: number[];
}

function FrequencyBar(props: FrequencyBarProps) {
  const isCenter =
    props.index === Math.floor(props.summedFrequencies.length / 2);

  let color = props.thinkingColor;
  let shadow = props.thinkingColorShadow;
  let transform;

  if (props.state === "listening" || props.state === "idle") {
    color = isCenter ? props.thinkingColor : props.frequencyColor;
    shadow = !isCenter ? undefined : shadow;
    transform = !isCenter ? "scale(1.0)" : "scale(1.2)";
  } else if (props.state === "speaking") {
    color = props.thinkingColor;
  } else if (props.state === "thinking") {
    color =
      props.index === props.thinkingIndex
        ? props.thinkingColor
        : props.frequencyColor;
    shadow = undefined;
    transform = props.thinkingIndex !== props.index ? "scale(1)" : "scale(1.1)";
  }

  return (
    <div
      className={cn(
        color,
        shadow,
        isCenter && props.state === "listening" ? "animate-pulse" : "",
      )}
      key={"frequency-" + props.index}
      style={{
        height:
          props.minBarHeight +
          props.frequency * (props.maxBarHeight - props.minBarHeight) +
          "px",
        borderRadius: props.borderRadius + "px",
        width: props.barWidth + "px",
        transition:
          "background-color 0.35s ease-out, transform 0.25s ease-out ",
        transform: transform,
      }}
    ></div>
  );
}
